import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, ScrollView, TouchableOpacity, Modal, DimensionValue, Animated, ActivityIndicator } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faRoute } from '@fortawesome/free-solid-svg-icons';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import Map from '../../../components/Map';
import Toast from 'react-native-toast-message';
import { appAgentService } from '../../../services/central-api/app-agent';
import { SceneChangedOccurrence } from '../../../components/app-agent/SceneChangedOccurrence';
import { FaceDetectedOccurrence } from '../../../components/app-agent/FaceDetectedOccurrence';
import { DefaultOccurrence } from '../../../components/app-agent/DefaultOccurrence';
import { PlateDetectedOccurrence } from '../../../components/app-agent/PlateDetectedOccurrence';
import { AreaInvasionOccurrence } from '../../../components/app-agent/AreaInvasionOccurrence';
import { MpPlateDetectedOccurrence } from '../../../components/app-agent/MpPlateDetectedOccurrence';
import { MpFaceDetectedOccurrence } from '../../../components/app-agent/MpFaceDetectedOccurrence';
import { AlarmCenterOccurrence } from '../../../components/app-agent/AlarmCenterOccurrence';
import { GuardianAppOccurrence } from '../../../components/app-agent/GuardianAppOccurrence';
import { SigrcOccurrence } from '../../../components/app-agent/SigrcOccurrence';
import { CortexPlateDetectedOccurrence } from '../../../components/app-agent/CortexPlateDetectedOccurrence';
import { Tabs } from '../../../components/Tabs';
import AgentOccurrenceHistoric from './AgentOccurrenceHistoric';
import useFilters from '../../../components/Filter/hooks/useFilters';
import AgentOccurrenceReport from './AgentOccurrenceReport';
import AgentOccurrenceInvolved from './AgentOccurrenceInvolved';
import AgentOccurrenceDisplaceQuestionary from './AgentOccurrenceDisplaceQuestionary';
import AgentOccurrencePoliceReport from './AgentOccurrencePoliceReport';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import ConfirmModal from '../../../components/ConfirmModal';
import { MpMobileAgentPlateDetectedOccurrence } from '../../../components/app-agent/MpMobileAgentPlateDetectedOccurrence';
import CustomButton from '../../../components/CustomButton';
import { SchoolAppOccurrence } from '../../../components/app-agent/SchoolAppOccurrence';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

interface OccurrenceDetailParams {
    navigation: Navigation;
    route: {
        params: {
            occurrenceId: string;
        };
    };
}

const animationTime = 300;

export default function AgentOccurrenceDetails({ navigation, route }: OccurrenceDetailParams) {
    const { styles, theme } = useStyles(styleSheet);

    const [step, setStep] = useState<'occurrenceDetail' | 'occurrenceReport' | 'occurrenceInvolved' | 'displaceQuestionary' | 'policeReport'>('occurrenceDetail');
    const [occurrence, setOccurrence] = useState<Occurrence>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const opacityAnim = useRef(new Animated.Value(0)).current;
    const scrollViewRef = useRef<ScrollView>(null);
    const [shadowOpen, setShadowOpen] = useState<boolean>(false);
    const [pin, setPin] = useState<LatLng>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [involvedVehicles, setInvolvedVehicles] = useState<InvolvedVehicle[]>([]);
    const [involvedObjects, setInvolvedObjects] = useState<InvolvedObject[]>([]);
    const [involvedPeople, setInvolvedPeople] = useState<InvolvedPerson[]>([]);
    const [agentUnit, setAgentUnit] = useState<{ id: number; displaced: boolean; OccurrenceUnit: OccurrenceUnit; }>();
    const [actingBody, setActingBody] = useState<ActingBodyConfigurations>();

    const [involved, setInvolved] = useState(0);
    const [handcuffed, setHandcuffed] = useState(0);
    const [shouldFinishOccurrence, setShouldFinishOccurrence] = useState<boolean>(false);
    const [displaceReason, setDisplaceReason] = useState<DisplaceReason>();
    const [policeReports, setPoliceReports] = useState<OccurrenceReport[]>([]);
    const [historic, setHistoric] = useState<OccurrenceReport[]>([]);

    const [showPoliceReportConfirmation, setShowPoliceReportConfirmation] = useState<boolean>(false);
    const [agentRejectPoliceReport, setAgentRejectPoliceReport] = useState<boolean>(false);

    const [filters, setFilters] = useFilters<{
        attachment: boolean | undefined;
        report: boolean | undefined;
        actions: boolean | undefined;
    }>({
        attachment: undefined,
        report: undefined,
        actions: undefined
    });

    async function getDisplaceReason(displaceReasonId: number) {
        try {
            const resp = await appAgentService.getUnitDisplaceReasonById(displaceReasonId);
            setDisplaceReason(resp);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function getOccurrence(occurrenceId: string) {
        try {
            const occurrence = await appAgentService.getOccurrenceById(Number(occurrenceId));
            setOccurrence(occurrence);
            setActingBody(occurrence.actingBody);
            if (occurrence.OccurrenceUnits && occurrence.OccurrenceUnits.length) {
                setAgentUnit(occurrence.OccurrenceUnits[0]);
            }

            const policeReports = await appAgentService.getPoliceReports({ occurrenceId: Number(occurrenceId) });
            setPoliceReports(policeReports);

            setInvolvedPeople(occurrence.involvedPeople);
            setInvolvedObjects(occurrence.involvedObjects);
            setInvolvedVehicles(occurrence.involvedVehicles);
        } catch (err) {
            console.error(err);
        }
    }

    async function getOccurrenceAttachmentHistoric(occurrenceId: number) {
        try {
            const resp = await appAgentService.getOccurrenceHistoricFiltered(occurrenceId, { attachment: true });
            setHistoric(resp);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        if (!occurrence) {
            return;
        }
        return setPin({ lat: occurrence.latitude, lng: occurrence.longitude });
    }, [occurrence]);

    async function loadPageInfo() {
        try {
            setIsLoading(true);
            await getOccurrence(route.params.occurrenceId);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            loadPageInfo();
            getOccurrenceAttachmentHistoric(Number(route.params.occurrenceId));
        }
    }, [isFocused, navigation, step]));

    useEffect(() => {
        if (modalOpen) {
            setShadowOpen(true);
            Animated.timing(opacityAnim, {
                toValue: 0.2,
                duration: animationTime,
                useNativeDriver: true,
            }).start();
        } else {
            Animated.timing(opacityAnim, {
                toValue: 0,
                duration: animationTime,
                useNativeDriver: true,
            }).start();
            setTimeout(() => setShadowOpen(false), animationTime);
        }
    }, [modalOpen]);

    if (!occurrence) {
        return <View style={styles.loadingView}>
            <MyAppText style={styles.loadingText}>
                {translate('loading')}...
            </MyAppText>
            <ActivityIndicator
                animating={true}
                style={styles.loading}
                size='large'
                color='#0071bc'
            >
            </ActivityIndicator>
        </View>;
    }

    return (
        <>
            {!isLoading ?
                <ScrollView contentContainerStyle={{ flex: 1 }} ref={scrollViewRef}>
                    <Tabs tabs={[{
                        key: 'generalInfo',
                        label: translate('general_info'),
                    }, {
                        key: 'historic',
                        label: translate('historic'),
                    }]}>
                        {({ selectedTab }) => {
                            if (!occurrence?.id) {
                                return <></>;
                            }
                            if (selectedTab == 'generalInfo') {
                                return <>
                                    {step == 'occurrenceDetail' && !isLoading &&
                                        <View style={styles.container}>
                                            {(occurrence.triggerType == 'plate_detected'
                                                || occurrence.triggerType == 'plate_detected_by_mobile_agent'
                                                || occurrence.triggerType == 'clone_suspect'
                                                || occurrence.triggerType == 'irregular_vehicle'
                                                || occurrence.triggerType == 'illegal_debris'
                                                || occurrence.triggerType == 'plate_detected_by_citizen_app'
                                            ) && occurrence.externalReference ?
                                                <PlateDetectedOccurrence
                                                    occurrence={occurrence}
                                                    event={occurrence.externalReference}
                                                    imageUrl={occurrence.resume.imageUrl}
                                                    navigation={navigation}
                                                /> : null}
                                            {occurrence.triggerType == 'mp_plate_detected' ?
                                                <MpPlateDetectedOccurrence occurrence={occurrence} navigation={navigation} imageUrl={occurrence.resume.imageUrl} /> : null}
                                            {occurrence.triggerType == 'mp_mobile_agent_plate_detected' ?
                                                <MpMobileAgentPlateDetectedOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {(
                                                occurrence.triggerType == 'face_detected' ||
                                                occurrence.triggerType == 'missing_face_detected'
                                            ) && occurrence.externalReference ?
                                                <FaceDetectedOccurrence
                                                    occurrence={occurrence}
                                                    event={{
                                                        ...occurrence.externalReference,
                                                        personDocument: occurrence.resume.personDocument
                                                    }}
                                                    imageUrl={occurrence.resume.imageUrl}
                                                    navigation={navigation}
                                                /> : null}
                                            {occurrence.triggerType == 'mp_face_detected' ?
                                                <MpFaceDetectedOccurrence occurrence={occurrence} navigation={navigation} imageUrl={occurrence.resume.imageUrl} /> : null}
                                            {occurrence.triggerType == 'defocus' || occurrence.triggerType == 'scenechangedetection' ?
                                                <SceneChangedOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {occurrence.triggerType == 'area_invasion' ?
                                                <AreaInvasionOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {occurrence.triggerType == 'alarm_center' ?
                                                <AlarmCenterOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {occurrence.triggerType == 'guardian_app' ?
                                                <GuardianAppOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {occurrence.triggerType == 'school_app' ?
                                                <SchoolAppOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {occurrence.triggerType == 'sigrc' ?
                                                <SigrcOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {occurrence.triggerType == 'cortex_plate_detected' ?
                                                <CortexPlateDetectedOccurrence occurrence={occurrence} navigation={navigation} /> : null}
                                            {occurrence.triggerType == null ?
                                                <DefaultOccurrence occurrence={occurrence} navigation={navigation} /> : null}


                                            {pin ?
                                                <View style={{ rowGap: 5 }}>
                                                    <CustomButton
                                                        onPress={() => {
                                                            if (pin) {
                                                                const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${pin.lat},${pin.lng}&travelmode=driving&dir_action=navigate`;
                                                                window.open(directionsUrl, '_blank');
                                                            }
                                                        }}
                                                        text={translate('route')}
                                                        type='primary'
                                                        icon={faRoute}
                                                    />
                                                    <TouchableOpacity onPress={() => setModalOpen(!modalOpen)}>
                                                        <View style={{ width: '100%', height: 450 }}>
                                                            <Map
                                                                pinnedMarker={pin}
                                                                draggable={false}
                                                                mapControls={false}
                                                            />
                                                        </View>
                                                    </TouchableOpacity>
                                                </View>
                                                : null
                                            }
                                            {agentUnit && agentUnit.OccurrenceUnit.isStarter ?
                                                <TouchableOpacity style={styles.involvedButton} onPress={() => {
                                                    setStep('occurrenceInvolved');
                                                    scrollViewRef.current?.scrollTo({ y: 0, animated: false });
                                                }}>
                                                    <MyAppText style={{ color: theme.colors.dataTable.actionIcon, fontSize: 16 }}>{translate('involved').toUpperCase()}</MyAppText>
                                                </TouchableOpacity> : <></>
                                            }

                                            <TouchableOpacity style={styles.addReport} onPress={() => {
                                                setShouldFinishOccurrence(false);
                                                setStep('occurrenceReport');
                                            }}>
                                                <MyAppText style={{ color: '#008E2C', fontSize: 16 }}>{translate('addReport').toUpperCase()} </MyAppText>
                                            </TouchableOpacity>
                                            <TouchableOpacity style={[styles.addReport, {
                                                borderColor: '#008E2C',
                                            }]} onPress={() => {
                                                setShouldFinishOccurrence(false);
                                                setStep('policeReport');
                                            }}>
                                                <MyAppText style={{ color: '#008E2C', fontSize: 16 }}>
                                                    {translate(!occurrence.policeReport ? 'addPoliceReport' : 'updatePoliceReport').toUpperCase()}
                                                </MyAppText>
                                            </TouchableOpacity>
                                            <TouchableOpacity style={styles.finishButton} onPress={async () => {
                                                if (actingBody?.askForPoliceReport && !occurrence.policeReport) {
                                                    setShowPoliceReportConfirmation(true);
                                                } else if (agentUnit && agentUnit.displaced && agentUnit.OccurrenceUnit.displaceReasonId) {
                                                    setStep('displaceQuestionary');
                                                    setInvolved(involvedPeople.length);
                                                    await getDisplaceReason(agentUnit.OccurrenceUnit.displaceReasonId);
                                                } else if (involvedPeople.filter((ip) => ip.handcuffUsed).length) {
                                                    setStep('displaceQuestionary');
                                                    setInvolved(involvedPeople.length);
                                                } else {
                                                    setShouldFinishOccurrence(true);
                                                    setStep('occurrenceReport');
                                                }
                                            }}>
                                                <MyAppText style={{ color: '#FF0027', fontSize: 16 }}>{translate('finish').toUpperCase()} </MyAppText>
                                            </TouchableOpacity>
                                        </View >
                                    }
                                    {step == 'occurrenceReport' && !isLoading &&
                                        <AgentOccurrenceReport
                                            occurrence={occurrence}
                                            handcuffed={handcuffed}
                                            displaceReason={displaceReason}
                                            involvedPeople={involvedPeople}
                                            shouldFinishOccurrence={shouldFinishOccurrence}
                                            setStep={setStep}
                                            navigation={navigation}
                                            actingBody={actingBody}
                                            historic={historic}
                                            rejectPoliceReport={agentRejectPoliceReport}
                                        />
                                    }
                                    {step == 'occurrenceInvolved' && !isLoading &&
                                        <AgentOccurrenceInvolved
                                            occurrence={occurrence}
                                            involvedObjects={involvedObjects}
                                            involvedPeople={involvedPeople}
                                            involvedVehicles={involvedVehicles}
                                            setInvolvedObjects={setInvolvedObjects}
                                            setInvolvedVehicles={setInvolvedVehicles}
                                            setInvolvedPeople={setInvolvedPeople}
                                            shouldFinishOccurrence={shouldFinishOccurrence}
                                            navigation={navigation}
                                            setStep={setStep}
                                            getOccurrence={getOccurrence}
                                        />
                                    }
                                    {step == 'displaceQuestionary' && !isLoading &&
                                        <AgentOccurrenceDisplaceQuestionary
                                            agentUnit={agentUnit}
                                            displaceReason={displaceReason}
                                            getOccurrence={getOccurrence}
                                            occurrence={occurrence}
                                            involved={involved}
                                            involvedPeople={involvedPeople}
                                            involvedObjects={involvedObjects}
                                            involvedVehicles={involvedVehicles}
                                            navigation={navigation}
                                            setInvolved={setInvolved}
                                            setInvolvedPeople={setInvolvedPeople}
                                            setStep={setStep}
                                            shouldFinishOccurrence={shouldFinishOccurrence}
                                            setShouldFinishOccurrence={setShouldFinishOccurrence}
                                            handcuffed={handcuffed}
                                            setHandcuffed={setHandcuffed}
                                        />
                                    }
                                    {step == 'policeReport' && !isLoading &&
                                        <AgentOccurrencePoliceReport
                                            occurrence={occurrence}
                                            policeReports={policeReports}
                                            setStep={setStep}
                                            getOccurrence={getOccurrence}
                                            shouldFinishOccurrence={shouldFinishOccurrence}
                                        />
                                    }
                                </>;
                            } else if (selectedTab == 'historic') {
                                return <AgentOccurrenceHistoric
                                    occurrenceId={occurrence.id}
                                    filters={filters}
                                    setFilters={setFilters}
                                />;
                            }
                        }}
                    </Tabs>
                </ScrollView >
                :
                <View style={styles.loadingView}>
                    <MyAppText style={styles.loadingText}>
                        {translate('processing')}...
                    </MyAppText>
                    <ActivityIndicator
                        animating={true}
                        style={styles.loading}
                        size='large'
                        color='#0071bc'
                    >
                    </ActivityIndicator>
                </View>
            }

            {shadowOpen ?
                <Animated.View style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#313131',
                    position: 'absolute',
                    opacity: opacityAnim
                }} />
                : null}
            <Modal
                animationType='slide'
                transparent={true}
                visible={modalOpen}
                onRequestClose={() => {
                    setModalOpen(!modalOpen);
                }}
            >
                <View style={styles.mapModalView}>
                    <TouchableOpacity style={styles.modalBackgroundView} onPress={() => setModalOpen(!modalOpen)} />
                    <View style={styles.topModalView}>
                        <TouchableOpacity onPress={() => setModalOpen(!modalOpen)}>
                            <FontAwesomeIcon icon={faChevronDown} style={{
                                marginLeft: 10,
                                marginRight: 5,
                                color: theme.colors.secondaryButton.text
                            }} />
                        </TouchableOpacity>
                        <MyAppText style={styles.topModalText}>{translate('location')}</MyAppText>
                    </View>
                    <View style={{ width: '100%', height: '80%' }}>
                        <Map
                            pinnedMarker={pin}
                        />
                    </View>
                </View>
            </Modal>
            <ConfirmModal
                customMessage={translate('addPoliceReportQuestion')}
                isModalVisible={showPoliceReportConfirmation}
                setModalVisible={setShowPoliceReportConfirmation}
                onSubmit={() => {
                    setAgentRejectPoliceReport(false);
                    setShouldFinishOccurrence(true);
                    setStep('policeReport');
                }}
                onCancel={() => {
                    setAgentRejectPoliceReport(true);
                    setShouldFinishOccurrence(true);
                    setStep('occurrenceReport');
                }}
            />
        </>
    );
}


const styleSheet = createStyleSheet((theme) => ({
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
        width: '20%',
        height: '20%',
    },
    loadingText: {
        color: theme.colors.loadingColor,
        textAlign: 'center',
        fontSize: 16
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center'
    },
    mapModalView: {
        flex: 1,
        justifyContent: 'flex-end',
    },
    modalBackgroundView: {
        height: `calc(20% - 50px)` as DimensionValue,
        width: '100%',
        borderColor: theme.colors.card.border,
        borderTopWidth: 1
    },
    topModalView: {
        height: 50,
        flexDirection: 'row',
        backgroundColor: theme.colors.card.background,
        alignItems: 'center'
    },
    topModalText: {
        color: theme.colors.hoverText,
        fontSize: 16
    },
    container: {
        flex: 1,
        padding: 16,
        gap: 20
    },
    finishButton: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: '#FF0027',
        padding: 14,
        display: 'flex',
        alignItems: 'center',
    },
    addReport: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: '#008E2C',
        padding: 14,
        display: 'flex',
        alignItems: 'center',
    },
    involvedButton: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.dataTable.actionIcon,
        padding: 14,
        display: 'flex',
        alignItems: 'center',
    },
}));
